import type { ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { PageHeaderActions } from './PageHeaderActions'
import type { PageAction, OverflowActions } from './pageActions'

export interface PageHeaderProps {
  title: string
  description?: React.ReactNode
  /**
   * Legacy freeform action slot — kept for un-migrated pages. Prefer the
   * structured `primaryAction` + `overflowActions` API (EP-106 Item 1), which
   * guarantees the mobile `[ solid ] [ ⋯ ]` invariant. A page is "migrated"
   * once it passes those instead of `actions`.
   */
  actions?: ReactNode
  /** EP-106 Item 1 — the one solid button (state-driven; may be omitted). */
  primaryAction?: PageAction
  /** EP-106 Item 1 — everything else (desktop inline, mobile overflow sheet). */
  overflowActions?: OverflowActions
  /** Force the mobile overflow into the Options sheet even for a single item
   *  (for long-labelled actions that would crowd the primary). */
  collapseOverflow?: boolean
  className?: string
  // TC-MUI-A5: an element rendered on the SAME line as the title, pinned
  // top-right, on MOBILE ONLY (`sm:hidden`). List pages pass their compact
  // property selector here so the property scope sits at page-header level on
  // phones (title top-left, selector top-right) instead of in the top app bar
  // or a separate card. On `sm`+ this is hidden — desktop keeps the in-body
  // picker card + the top-bar switcher.
  titleAction?: ReactNode
  // Show titleAction on every screen size, not only on a phone. For a page
  // with no header actions whose title-row control applies everywhere (the
  // dashboard's building picker).
  titleActionOnDesktop?: boolean
  // When true, render a small spinner next to the title indicating an in-flight
  // background refetch. Pages should pass `isFetching && !isLoading` so the
  // initial skeleton (driven by isLoading) isn't doubled with a spinner.
  // Per CLAUDE.md convention 30 — global staleTime is 1 min, so background
  // refetches happen on navigation back to a cached page; the spinner makes
  // them visible without changing data.
  isRefreshing?: boolean
}

export function PageHeader({ title, description, actions, primaryAction, overflowActions, collapseOverflow, titleAction, titleActionOnDesktop = false, className = '', isRefreshing = false }: PageHeaderProps) {
  const { t } = useTranslation('common')
  const hasStructuredActions =
    primaryAction !== undefined || (overflowActions !== undefined && overflowActions.length > 0)
  return (
    <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 ${className}`}>
      <div className="min-w-0 sm:flex-1">
        {/* Title row: title top-left, optional titleAction top-right (mobile only).
            items-start so a tall Thai 2xl title and the short selector align at
            the top rather than the selector floating mid-title. */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <h1 className="text-2xl font-bold truncate leading-tight">{title}</h1>
            {isRefreshing && (
              <Loader2
                className="w-4 h-4 animate-spin text-base-content/60 shrink-0"
                aria-label={t('labels.refreshing')}
                data-testid="page-refreshing"
              />
            )}
          </div>
          {titleAction && <div className={`${titleActionOnDesktop ? '' : 'sm:hidden '}shrink-0`}>{titleAction}</div>}
        </div>
        {description && <p className="text-base-content/60 mt-2">{description}</p>}
      </div>
      {hasStructuredActions ? (
        <PageHeaderActions primaryAction={primaryAction} overflowActions={overflowActions} collapseOverflow={collapseOverflow} />
      ) : (
        actions && (
          // TC-MUI-A5: on mobile the action buttons stay on ONE line and scroll
          // horizontally (flex-nowrap + overflow-x-auto) instead of wrapping to a
          // second row; children never shrink. `overflow-y-hidden` prevents any
          // vertical overflow/scrollbar on the strip. From `sm` up they wrap as
          // before. The negative margin lets the scroll strip bleed to the viewport
          // edge so it reads as scrollable; `no-scrollbar` hides the bar.
          // `justify-end` right-aligns the strip on mobile so un-migrated
          // single/legacy actions match the migrated PageHeaderActions pattern
          // (desktop is unaffected — the block is already content-width on the
          // right via the header's justify-between).
          <div className="flex flex-nowrap sm:flex-wrap items-center justify-end gap-2 overflow-x-auto overflow-y-hidden no-scrollbar [&>*]:shrink-0 -mx-4 px-4 py-1 sm:mx-0 sm:px-0 sm:py-0">
            {actions}
          </div>
        )
      )}
    </div>
  )
}
